import { useState } from 'react';
import { Button } from '@/components/ui/Button';

interface Step7Props {
  onNext: (data: Step7Data) => void;
  onBack: () => void;
  initialData?: Step7Data;
}

export interface Step7Data {
  timeline: string;
  budgetRange: string;
  notes?: string;
}

const timelines = [
  { value: 'asap', label: 'ASAP', description: 'Start immediately' },
  { value: '1-2weeks', label: '1-2 weeks', description: 'Quick turnaround' },
  { value: '1month', label: '1 month', description: 'Standard timeline' },
  { value: '2-3months', label: '2-3 months', description: 'Complex project' },
  { value: 'flexible', label: 'Flexible', description: 'No rush' },
];

const budgetRanges = [
  { value: '5k-15k', label: 'R5,000 - R15,000', description: 'Basic features' },
  { value: '15k-50k', label: 'R15,000 - R50,000', description: 'Standard project' },
  { value: '50k-150k', label: 'R50,000 - R150,000', description: 'Advanced features' },
  { value: '150k+', label: 'R150,000+', description: 'Enterprise solution' },
  { value: 'not_sure', label: 'Not sure', description: 'Need guidance' },
];

export const Step7TimelineBudget = ({ onNext, onBack, initialData }: Step7Props) => {
  const [timeline, setTimeline] = useState(initialData?.timeline || '');
  const [budgetRange, setBudgetRange] = useState(initialData?.budgetRange || '');
  const [notes, setNotes] = useState(initialData?.notes || '');

  const handleSubmit = () => {
    if (timeline && budgetRange) {
      onNext({ timeline, budgetRange, notes });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Timeline & Budget</h2>
        <p className="text-gray-600 mt-2">When do you need it and what's your budget?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Timeline *</label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {timelines.map((t) => (
            <div
              key={t.value}
              onClick={() => setTimeline(t.value)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                timeline === t.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <h4 className="font-semibold text-gray-900">{t.label}</h4>
              <p className="text-sm text-gray-600">{t.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Budget Range *</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {budgetRanges.map((b) => (
            <div
              key={b.value}
              onClick={() => setBudgetRange(b.value)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                budgetRange === b.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <h4 className="font-semibold text-gray-900">{b.label}</h4>
              <p className="text-sm text-gray-600">{b.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
          Additional Notes (Optional)
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Any other requirements or constraints we should know about..."
        />
      </div>

      <div className="flex justify-between">
        <Button onClick={onBack} variant="outline" className="px-8">
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!timeline || !budgetRange}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
